import {
    CANCEL_RUN,
    CREATE_RUN,
    CREATE_RUN_FAIL,
    CREATE_RUN_SUCCESS,
    DELETE_RUN,
    FETCH_RUN_FAIL,
    FETCH_RUN_RESULTS_FAIL,
    FETCH_RUN_RESULTS_SUCCESS,
    FETCH_RUN_RETRY,
    FETCH_RUN_SUCCESS,
    FETCH_RUN_TO_COPY,
    FETCH_RUN_TO_COPY_FAIL,
    FETCH_RUN_TO_COPY_SUCCESS,
    FETCH_RUNS,
    FETCH_RUNS_FAIL,
    FETCH_RUNS_GAVE_UP,
    FETCH_RUNS_RETRY,
    FETCH_RUNS_SUCCESS,
    FETCH_SCENARIOS,
    FETCH_SCENARIOS_FAIL,
    FETCH_SCENARIOS_SUCCESS,
    FETCH_SIMULATION_LIMITS,
    FETCH_SIMULATION_LIMITS_FAIL,
    FETCH_SIMULATION_LIMITS_SUCCESS,
    FOLLOW_RUN,
    RUN_ACTION_FAIL,
    RUN_ACTION_SUCCESS,
    STOP_FOLLOWING_RUN
} from './actionTypes';
import * as _ from 'lodash';
import {
    defaultFirstPage,
    defaultItemsPerPage
} from '../../../../../services/Paging';
import { Connection } from '../../simulation.model';

/* ---- The list of runs ---- */

export const runsListInitialState = {
    data: [],
    pagination: {
        pageSize: defaultItemsPerPage,
        page: defaultFirstPage,
        hasNextPage: false
    },
    sort: {},
    // The runs that have a cancel or a delete in progress, by id
    busy: {},
    // Whether what is shown is up to date, while the list is being followed
    connection: Connection.OK,
    error: null,
    loading: false,
    loaded: false
};

export function runsListReducer(state = runsListInitialState, action) {
    switch (action.type) {
        case FETCH_RUNS: {
            const { pageSize, page, sortType, sortOrder } = action.payload;

            return {
                ...state,
                pagination: {
                    ...state.pagination,
                    pageSize: pageSize || state.pagination.pageSize,
                    page: page || state.pagination.page
                },
                sort:
                    sortType || sortOrder
                        ? { sortType, sortOrder }
                        : state.sort,
                loading: true
            };
        }
        case FETCH_RUNS_SUCCESS:
            return {
                ...state,
                data: action.payload.result || [],
                pagination: {
                    ...state.pagination,
                    hasNextPage: !!action.payload.hasNext
                },
                error: null,
                connection: Connection.OK,
                loaded: true,
                loading: false
            };
        case FETCH_RUNS_RETRY:
            return { ...state, connection: Connection.RECONNECTING };
        case FETCH_RUNS_GAVE_UP:
            return {
                ...state,
                connection: Connection.LOST,
                error: action.payload
            };
        case FETCH_RUNS_FAIL:
            return {
                ...state,
                error: action.payload,
                loading: false
            };
        case CANCEL_RUN:
        case DELETE_RUN:
            return {
                ...state,
                busy: { ...state.busy, [action.payload.runId]: true }
            };
        case RUN_ACTION_SUCCESS:
        case RUN_ACTION_FAIL:
            return {
                ...state,
                busy: _.omit(state.busy, action.payload.runId)
            };
        default:
            return state;
    }
}

/* ---- The catalog of scenarios and the limits, which the form of a run is made from ---- */

const catalogInitialState = {
    data: [],
    error: null,
    loading: false,
    loaded: false
};

export function scenariosReducer(state = catalogInitialState, action) {
    switch (action.type) {
        case FETCH_SCENARIOS:
            return { ...state, loading: true };
        case FETCH_SCENARIOS_SUCCESS:
            return {
                ...state,
                data: action.payload,
                error: null,
                loading: false,
                loaded: true
            };
        case FETCH_SCENARIOS_FAIL:
            return { ...state, error: action.payload, loading: false };
        default:
            return state;
    }
}

export function limitsReducer(
    state = { ...catalogInitialState, data: null },
    action
) {
    switch (action.type) {
        case FETCH_SIMULATION_LIMITS:
            return { ...state, loading: true };
        case FETCH_SIMULATION_LIMITS_SUCCESS:
            return {
                ...state,
                data: action.payload,
                error: null,
                loading: false,
                loaded: true
            };
        case FETCH_SIMULATION_LIMITS_FAIL:
            return { ...state, error: action.payload, loading: false };
        default:
            return state;
    }
}

/* ---- The run that is open: being followed, with its results ---- */

export const runInitialState = {
    runId: null,
    data: null,
    results: null,
    // A run that was asked for and does not exist (any more)
    notFound: false,
    // Whether what is shown is up to date, while the run is being followed
    connection: Connection.OK,
    error: null,
    loading: false,
    submitting: false,
    // Why the last run that was asked for was refused, until another is asked for: its activeRunId names the run that is in the way
    refusal: null,
    acting: false
};

export function runReducer(state = runInitialState, action) {
    switch (action.type) {
        case FOLLOW_RUN: {
            const { runId } = action.payload;

            // Following the run that is already open keeps what is shown; another run starts from nothing
            return state.runId === runId
                ? { ...state, loading: !state.data }
                : { ...runInitialState, runId, loading: true };
        }
        case STOP_FOLLOWING_RUN:
            return { ...state, loading: false };
        case FETCH_RUN_SUCCESS:
            return {
                ...state,
                data: action.payload,
                error: null,
                notFound: false,
                connection: Connection.OK,
                loading: false
            };
        case FETCH_RUN_RETRY:
            return { ...state, connection: Connection.RECONNECTING };
        case FETCH_RUN_FAIL: {
            const notFound = _.get(action.payload, 'statusCode') === 404;

            // What was last read stays, and is not made a failed run: it is the connection that failed
            return {
                ...state,
                error: action.payload,
                notFound,
                connection: notFound ? state.connection : Connection.LOST,
                loading: false
            };
        }
        case FETCH_RUN_RESULTS_SUCCESS:
            return { ...state, results: action.payload };
        case FETCH_RUN_RESULTS_FAIL:
            return state;
        case CREATE_RUN:
        case CREATE_RUN_SUCCESS:
            return {
                ...state,
                submitting: action.type === CREATE_RUN,
                refusal: null
            };
        case CREATE_RUN_FAIL:
            return { ...state, submitting: false, refusal: action.payload };
        case CANCEL_RUN:
        case DELETE_RUN:
            return { ...state, acting: true };
        case RUN_ACTION_SUCCESS:
        case RUN_ACTION_FAIL:
            return { ...state, acting: false };
        default:
            return state;
    }
}

/* ---- The run that a new run is made from: "Run again" ---- */

export const runToCopyInitialState = {
    runId: null,
    data: null,
    error: null,
    loading: false
};

export function runToCopyReducer(state = runToCopyInitialState, action) {
    switch (action.type) {
        case FETCH_RUN_TO_COPY:
            return {
                ...runToCopyInitialState,
                runId: action.payload.runId,
                loading: true
            };
        case FETCH_RUN_TO_COPY_SUCCESS:
            return { ...state, data: action.payload, loading: false };
        case FETCH_RUN_TO_COPY_FAIL:
            return { ...state, error: action.payload, loading: false };
        default:
            return state;
    }
}
